import React, { forwardRef, useId } from 'react'

import {
  errorClasses,
  getErrorInputClasses,
  helperClasses,
  labelClasses,
} from '@/app/components/Form/styles'
import { cn } from '@/app/utils/style'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  variant?: 'default' | 'small'
  labelClassName?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      className,
      id,
      required,
      variant = 'default',
      labelClassName,
      ...props
    },
    ref,
  ) => {
    const reactId = useId()
    const inputId =
      id ||
      (label && `input-${label.toLowerCase().replace(/\s+/g, '-')}`) ||
      (props.name && `input-${props.name}`) ||
      reactId
    const sizeClass = variant === 'small' ? 'input-smallLight' : 'input-defaultLight'

    return (
      <div className="flex flex-col flex-1">
        {label && (
          <label htmlFor={inputId} className={cn(labelClasses, labelClassName)}>
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={cn(sizeClass, getErrorInputClasses(error), 'w-full box-border', className)}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          required={required}
          {...props}
        />
        {error && (
          <span id={`${inputId}-error`} className={errorClasses}>
            {error}
          </span>
        )}
        {!error && helperText && (
          <span id={`${inputId}-helper`} className={helperClasses}>
            {helperText}
          </span>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'
